import { mapAggregateToRollup } from "../../utils/metrics";
import type { RollupWithTagsAndCountsAndRange } from "../../types/types";
import { GET_PRODUCTION_AGGREGATES_QUERY } from "../../providers/AskrProvider";
import { ALL_SHIFT_NAME_DISPLAY } from "../../components/FlightTrends/TrendsFilters/filtersDrawerUtils";
import useQueryWithErrorHandling from "../../utils/useQuery";
import { FetchPolicy } from "@apollo/client";
import { useRef, useTransition } from "react";
import { useTrendChartBarRangesForBarstacks } from "utils/customHooks/trendChartHooks";

export const FETCH_POLICY: FetchPolicy = "no-cache";

// no frequency: single aggregate (butterfly, pnl summary chart)
export const AGGREGATE_NO_FREQUENCY_QUERY = GET_PRODUCTION_AGGREGATES_QUERY({
  willAggregateOnFrequency: false,
});
// frequency: for trend chart bars
export const AGGREGATE_ON_FREQUENCY_QUERY = GET_PRODUCTION_AGGREGATES_QUERY({
  willAggregateOnFrequency: true,
});
// frequency shiftly: for trend chart bars, pnl summary chart, or butterfly, on trends page when filtered by shifts
// -- we get all the shift aggregates in the store, then filter and manually roll them up in the components
export const aggregateOnFrequencyShiftlyQuery = GET_PRODUCTION_AGGREGATES_QUERY(
  {
    willAggregateOnFrequency: true,
    isShiftly: true,
  }
);

export function isLargeQueryError(err: any) {
  return JSON.stringify(err).includes(
    "Large data queries outside of the cached range are not supported yet"
  );
}

export type queryAndUpdateAggregateProps = {
  skip?: boolean;
  query: any;
  client: any;
  onError: any;
  dispatchFn: (rollup: RollupWithTagsAndCountsAndRange) => any;
  variables: any;
  pollInterval?: number | null;
  queryTitle: string;
};

export function useQueryAndUpdateMultipleAggregates({
  skip = false,
  query,
  variables,
  client,
  dispatchFn,
  pollInterval = null,
  onError,
  queryTitle,
}: queryAndUpdateAggregateProps) {
  const numRollupsExpected = useTrendChartBarRangesForBarstacks().filter(
    d => d.ge < Date.now()
  ).length; // expect data returned for each bar not in the future
  const retries = useRef(0);
  const [, startTransition] = useTransition();
  const { error, loading, refetch } = useQueryWithErrorHandling(
    queryTitle,
    query,
    {
      // run it if the trends page is not filtered to "all" shifts
      ...(pollInterval ? { pollInterval } : {}),
      client,
      variables,
      fetchPolicy: FETCH_POLICY,
      displayName: queryTitle, // shows up in React Dev Tools
      partialRefetch: true,
      onError,
      onCompleted: data => {
        if (data && !loading && !error) {
          const { productionAggregates } = data;
          if (!productionAggregates) {
            return;
          }
          if (
            productionAggregates.length < numRollupsExpected &&
            Math.abs(productionAggregates.length - numRollupsExpected) > 1 && // allow +/-1 for DST changes
            // using "<" instead of "!==" because sometimes more than expected are returned... e.g. https://localhost:3000/amforge/trends?binSize=weeks&ge=1704085200000&lt=1704907167982&workSchedule=1 -> 2 expected, 3 returned
            // dataConsistency.spec test mocks this data without respecting the expected # rollups
            !productionAggregates[0].isFake
          ) {
            console.log(
              "⚠ wrong number of rollups returned from query",
              variables,
              {
                numRollupsExpected,
                numRollupsReceived: productionAggregates.length,
              }
            );
            if (retries.current <= 5) {
              console.log("refetching...");
              retries.current += 1;
              refetch();
            }
            return;
          } else {
            retries.current = 0;
          }
          const newRollupsArray =
            productionAggregates.map(mapAggregateToRollup);

          // for multiple-aggregate queries, we get the array back
          const newRollupsArrActual = newRollupsArray;
          startTransition(() => {
            dispatchFn(newRollupsArrActual);
          });
        }
      },
    }
  );
}

export function getIsShiftlyQuery(
  trendsPageFilterStateShiftName: string,
  focusRange: { ge: number; lt: number } | null,
  isTrendsPage: boolean
) {
  if (!focusRange) {
    return false;
  }
  const isTrendsPageFilteredByShift =
    trendsPageFilterStateShiftName !== ALL_SHIFT_NAME_DISPLAY;

  // for shiftly queries, store all shift aggregates
  // and filter by shift name on trends page
  return isTrendsPage && isTrendsPageFilteredByShift;
}
